import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Flex, Select, Text, TextInput } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import COLORS from '../../constants/colors';
import { createArea, toggleArea, updateArea } from '../../services/area';
import { NotificationUtil } from '../../utils/notifications';
import { AddAreaSchema, UpdateAreaSchema } from '../../validators/area';

const AreaForm = ({ defaultValues, update, onUpdate, regionPagelessdata }) => {
  const queryClient = useQueryClient();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    defaultValues: defaultValues ? defaultValues : {},
    resolver: yupResolver(update ? UpdateAreaSchema : AddAreaSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    reset();
  }, []);

  const onSubmit = (data) => {
    ConfirmModal(data);
  };

  const ConfirmModal = (values) => {
    openConfirmModal({
      title: 'Confirm',
      styles: () => ({
        title: {
          fontSize: '22px',
          fontWeight: 'bold',
        },
      }),
      children: (
        <Text size="sm">
          Are you sure you {update ? 'update' : 'create'} this area?
        </Text>
      ),
      confirmProps: { color: 'red' },
      labels: { confirm: 'Confirm', cancel: 'Cancel' },
      onConfirm: () => {
        if (update) {
          updateMutate({
            id: defaultValues.id,
            ...values,
          });
        } else {
          createMutate(values);
        }
      },
    });
  };

  const ConfirmDeleteModal = (values) => {
    openConfirmModal({
      title: 'Confirm',
      styles: () => ({
        title: {
          fontSize: '22px',
          fontWeight: 'bold',
        },
      }),
      children: <Text size="sm">Are you sure you deactivate this area?</Text>,
      confirmProps: { color: 'red' },
      labels: { confirm: 'Confirm', cancel: 'Cancel' },
      onConfirm: () => {
        deleteMutate(values);
      },
    });
  };

  const { mutate: createMutate, isLoading: isCreating } = useMutation({
    mutationFn: async (values) => await createArea(values),
    onSuccess: () => {
      onUpdate();
      callRefetch();
    },
    onError: (error) => {
      NotificationUtil({
        success: false,
        title: 'Error',
        message: error.response.data.message,
      });
    },
  });

  const { mutate: updateMutate, isLoading: isUpdating } = useMutation({
    mutationFn: async (values) => await updateArea(values),
    onSuccess: () => {
      onUpdate();
    },
    onError: (error) => {
      NotificationUtil({
        success: false,
        title: 'Error',
        message: error.response.data.message,
      });
    },
  });

  const { mutate: deleteMutate, isLoading: isDeleting } = useMutation({
    mutationFn: async (values) => await toggleArea(values),
    onSuccess: () => {
      onUpdate();
    },
    onError: (error) => {
      NotificationUtil({
        success: false,
        title: 'Error',
        message: error.response.data.message,
      });
    },
  });

  const callRefetch = async () => {
    await queryClient.refetchQueries({
      queryKey: ['region-pagelessData'],
      type: 'active',
    });
    await queryClient.refetchQueries({
      queryKey: ['area-pagelessData'],
      type: 'active',
    });
    await queryClient.refetchQueries({
      queryKey: ['distributor-pagelessData'],
      type: 'active',
    });
  };

  return (
    <div
      style={{
        height: '90vh',
      }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="accounts-input">
          <Controller
            name="regionId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                data={
                  regionPagelessdata?.map((item) => {
                    return {
                      label: item.name,
                      value: item.id,
                    };
                  }) || []
                }
                searchable
                label="Region"
                placeholder="Select region"
                dropdownPosition="bottom"
              />
            )}
          />
          {errors && errors.regionId && <p>{errors?.regionId?.message}</p>}
        </div>
        <div className="accounts-input">
          <TextInput
            {...register('name')}
            label="Name"
            placeholder="Enter name"
            error={errors?.name?.message}
          />
        </div>

        <Flex gap={15} py={8} mt={10}>
          {update && (
            <Button
              disabled={isSubmitting}
              sx={{
                flex: 1,
              }}
              onClick={() => {
                ConfirmDeleteModal({
                  status: !defaultValues.isActive,
                  id: defaultValues.id,
                });
              }}
              color={defaultValues.isActive ? 'red' : 'green'}>
              {defaultValues.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting || isCreating || isUpdating || isDeleting}
            disabled={isSubmitting || !isValid}
            sx={{
              flex: 1,
              backgroundColor: COLORS.primary,
            }}>
            {update ? 'Update' : 'Create'}
          </Button>
        </Flex>
      </form>
    </div>
  );
};

export default AreaForm;
